import React, { useEffect, useRef, useState } from "react";
import MenuItem from "./MenuItem";
import Button from "./Button";
import Logo from "./Logo";
import { ToggleSwitch } from "./Inputs";

export default function NavBar({
  items = [],
  activeId,
  onNavigate,
  showBrand = true,
  // Logo area: defaults handled by Logo component (placeholders)
  // Example: { svg: <Logo/>, alt: 'Brand', href: '/', width: 32, height: 32 }
  logo = {},
  vertical = false,
  // Special Actions section (Section 3)
  // If undefined, defaults to cart + settings to preserve current behavior.
  // Pass [] to hide actions entirely.
  actions,
  // Actions layout:
  // - "auto": overflow on mobile prop, inline otherwise (default)
  // - "overflow": always show 3-dot overflow menu
  // - "inline": always render actions inline
  actionsDisplay = "auto",
  mobile = false,
}) {
  const navRef = useRef(null);
  const menuRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [navStyleClass, setNavStyleClass] = useState("services-style");
  const [followOffset, setFollowOffset] = useState("0px");
  const [internalActiveId, setInternalActiveId] = useState(
    activeId ?? items[0]?.id ?? items[0]?.label ?? null,
  );
  const defaultActions = [
    {
      id: "cart",
      componentType: "button",
      icon: "shopping_cart",
      ariaLabel: "Cart",
    },
    {
      id: "settings",
      componentType: "button",
      icon: "settings",
      ariaLabel: "Settings",
    },
  ];
  const resolvedActions = actions === undefined ? defaultActions : actions;
  const actionList = Array.isArray(resolvedActions)
    ? resolvedActions.filter(Boolean)
    : [];
  const rootClass = [
    "rm-navbar",
    navStyleClass,
    vertical ? "rm-navbar--vertical" : "",
    open ? "is-open" : "",
  ]
    .filter(Boolean)
    .join(" ");
  const showOverflowActions =
    actionsDisplay === "overflow" || (actionsDisplay === "auto" && mobile);
  const effectiveActiveId = internalActiveId;
  const isItemActive = (it, idx) => {
    const itemId = it.id ?? it.label ?? `item-${idx}`;
    if (it.id !== undefined && it.id !== null) {
      return itemId === effectiveActiveId;
    }
    if (effectiveActiveId !== undefined && effectiveActiveId !== null) {
      return itemId === effectiveActiveId;
    }
    return it.active === true;
  };

  useEffect(() => {
    if (activeId !== undefined) {
      setInternalActiveId(activeId);
    }
  }, [activeId]);

  useEffect(() => {
    const activeItem = items.find((it, idx) => {
      const itemId = it.id ?? it.label ?? `item-${idx}`;
      return itemId === effectiveActiveId;
    });
    const where = activeItem?.dataWhere || activeItem?.where;
    if (where) {
      setNavStyleClass(`${where}-style`);
    }
  }, [items, effectiveActiveId]);

  useEffect(() => {
    const updateFollowOffset = () => {
      const menuEl = menuRef.current;
      if (!menuEl) return;
      const activeEl = menuEl.querySelector(".rm-navbar__menu-item.is-active");
      if (!activeEl) {
        setFollowOffset("0px");
        return;
      }
      setFollowOffset(`${activeEl.offsetLeft}px`);
    };

    updateFollowOffset();
    window.addEventListener("resize", updateFollowOffset);
    return () => {
      window.removeEventListener("resize", updateFollowOffset);
    };
  }, [items, effectiveActiveId, open, mobile, vertical]);

  const activateNavItem = (item, itemId, isActive) => {
    const isFollow = item?.follow === true;
    if (isActive || isFollow) return;
    const where = item?.dataWhere || item?.where || "services";
    setNavStyleClass(`${where}-style`);
    setInternalActiveId(itemId);
  };
  const handleMenuSelect = (item, itemId, isActive) => {
    activateNavItem(item, itemId, isActive);
    onNavigate?.(itemId);
    setOpen(false);
  };

  const renderActionNode = (action, idx, mobileLayout = false) => {
    if (!action || typeof action !== "object") return null;

    const {
      id,
      componentType = "button",
      label = "",
      className = "",
      ariaLabel,
      onClick,
      ...componentProps
    } = action;
    const key = id ?? `action-${idx}`;
    let node = null;

    if (componentType === "toggle") {
      const {
        toggleId,
        leftLabel,
        rightLabel,
        defaultChecked = false,
        ...toggleProps
      } = componentProps;

      node = (
        <div className={className} aria-label={ariaLabel || label || id}>
          {leftLabel ? (
            <span className="rm-navbar__action-label rm-theme-toggle__text">
              {leftLabel}
            </span>
          ) : null}
          <ToggleSwitch
            key={`${key}-${defaultChecked ? "1" : "0"}`}
            id={toggleId || id || `toggle-${idx}`}
            label={label}
            defaultChecked={defaultChecked}
            onClick={(checked) => onClick?.(checked, id)}
            {...toggleProps}
          />
          {rightLabel ? (
            <span className="rm-navbar__action-label rm-theme-toggle__text">
              {rightLabel}
            </span>
          ) : null}
        </div>
      );
    } else if (typeof componentType === "function") {
      const CustomComponent = componentType;
      node = (
        <CustomComponent
          id={id}
          label={label}
          className={className}
          ariaLabel={ariaLabel}
          onClick={onClick}
          {...componentProps}
        />
      );
    } else {
      const {
        icon,
        size = "sm",
        iconOnly = !label,
        ...buttonProps
      } = componentProps;

      node = (
        <Button
          icon={icon}
          label={label}
          iconOnly={iconOnly}
          size={size}
          aria-label={ariaLabel || label || id}
          className={[className].filter(Boolean).join(" ")}
          onClick={() => onClick?.(id)}
          {...buttonProps}
        />
      );
    }

    return mobileLayout ? (
      <li role="none" key={key}>
        {node}
      </li>
    ) : (
      <React.Fragment key={key}>{node}</React.Fragment>
    );
  };

  return (
    <nav className={rootClass} ref={navRef}>
      {/* Tablet toggle (hamburger/X) — visible via CSS on tablet only */}
      <Button
        className="rm-navbar__toggle btn-icon-only"
        icon={open ? "close" : "menu"}
        iconOnly
        size="sm"
        aria-label={open ? "Close menu" : "Open menu"}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      />
      {/* Section 1: Branding */}
      {showBrand && !vertical ? (
        <div
          className="rm-navbar__brand"
          role="button"
          tabIndex={0}
          aria-expanded={open}
          aria-controls="rm-navbar-menu"
          onClick={() => {
            // Toggle only on mobile viewport
            if (
              typeof window !== "undefined" &&
              window.matchMedia &&
              window.matchMedia("(max-width: 767px)").matches
            ) {
              setOpen((v) => !v);
            }
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              if (
                typeof window !== "undefined" &&
                window.matchMedia &&
                window.matchMedia("(max-width: 767px)").matches
              ) {
                setOpen((v) => !v);
              }
            }
          }}
        >
          {logo ? (
            <Logo
              partClass="rm-navbar__logo"
              gap={logo.gap || "var(--paddingSM)"}
              href={logo.href}
              alt={logo.alt}
              icon={logo.icon}
              wordmark={logo.wordmark}
              showIcon={logo.showIcon ?? true}
              showWordmark={logo.showWordmark ?? true}
            />
          ) : (
            <div className="rm-navbar__logo" aria-hidden />
          )}
        </div>
      ) : null}

      {/* Section 2: Main Navigation */}
      <ul
        ref={menuRef}
        id="rm-navbar-menu"
        className="rm-navbar__menu"
        role="menubar"
        style={{
          "--rm-nav-count": String(Math.max(items.length, 1)),
          "--rm-follow-index": followOffset,
        }}
      >
        {items.map((it, idx) => {
          const itemId = it.id ?? it.label ?? `item-${idx}`;
          const isActive = isItemActive(it, idx);
          return (
            <li
              key={itemId}
              data-index={idx}
              data-where={it.dataWhere || it.where || "services"}
              className={[
                "rm-navbar__menu-item",
                isActive ? "is-active" : "",
                it.follow ? "follow" : "",
              ]
                .filter(Boolean)
                .join(" ")}
              role="none"
            >
              {vertical ? (
                <MenuItem
                  role="menuitem"
                  showLabel={it.showLabels}
                  icon={it.icon}
                  label={it.label}
                  className="rm-navbar__menu-btn"
                  onClick={() => {
                    handleMenuSelect(it, itemId, isActive);
                  }}
                />
              ) : mobile ? (
                <Button
                  role="menuitem"
                  icon={it.icon || "circle"}
                  iconOnly
                  size="sm"
                  className="rm-navbar__menu-btn text-primary"
                  onClick={() => {
                    handleMenuSelect(it, itemId, isActive);
                  }}
                />
              ) : (
                <MenuItem
                  role="menuitem"
                  showLabel={it.showLabels}
                  icon={it.icon}
                  label={it.label}
                  className="rm-navbar__menu-btn"
                  onClick={() => {
                    handleMenuSelect(it, itemId, isActive);
                  }}
                />
              )}
            </li>
          );
        })}
      </ul>

      {/* Section 3: Special Actions (hide on mobile; show overflow) */}
      {actionList.length > 0 ? (
        showOverflowActions ? (
          <div className="rm-iconbar__overflow" aria-label="More actions">
            <Button
              icon="more_vert"
              iconOnly
              size="sm"
              className="rm-iconbar__btn rm-iconbar__btn"
              aria-haspopup="menu"
              onClick={(e) => {
                const menu = e.currentTarget.nextElementSibling;
                if (menu) menu.classList.toggle("is-open");
              }}
            />
            <ul
              className="rm-iconbar__menu rm-card rm-card-elevated"
              role="menu"
            >
              {actionList.map((act, idx) => renderActionNode(act, idx, true))}
            </ul>
          </div>
        ) : (
          <div className="rm-navbar__actions" aria-label="Utilities">
            {actionList.map((act, idx) => renderActionNode(act, idx))}
          </div>
        )
      ) : null}
    </nav>
  );
}
